/**
 * Mutations for keeping a document's proposals honest about ServiceTitan:
 * re-reading what they rest on, and re-running the agent.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  ApAgentRun,
  ApBasisRefusal,
  ApDocumentType,
} from "@/accounts-payable/types";
import { useApi } from "@/core/api";
import { ApiError } from "@/core/api/types";
import { AP_DOCUMENT_ENDPOINTS, agentRunsKey, reviewQueueKey } from "./keys";

/**
 * Re-read ServiceTitan for what a document's open proposals rest on.
 * POST /businesses/:id/ap/{invoices|receipts}/:documentId/recheck
 *
 * Responds with the run, each action carrying `st_changes` (and
 * `approval_blocked_reason` when ServiceTitan would refuse it now). 502 when
 * ServiceTitan can't be read, after saving the checks made before the failed
 * read, so the caches are refreshed either way.
 */
export const useRecheckDocument = (
  businessId: string | null | undefined,
  documentType: ApDocumentType,
) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { path } = AP_DOCUMENT_ENDPOINTS[documentType];
  return useMutation({
    mutationFn: (documentId: string) =>
      api
        .post<ApAgentRun>(
          `/businesses/${businessId}/ap/${path}/${documentId}/recheck`,
        )
        .then((response) => response.data),
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: agentRunsKey(businessId),
      });
      void queryClient.invalidateQueries({
        queryKey: reviewQueueKey(businessId),
      });
    },
  });
};

/**
 * Run the agent again on a document.
 * POST /businesses/:id/ap/{invoices|receipts}/:documentId/run_agent
 *
 * Supersedes the last run's open proposals, then queues a run: 202 with the
 * new run, or 409 when one is already queued or running (the proposals are
 * superseded all the same, so the caches are refreshed either way).
 */
export const useRunAgent = (
  businessId: string | null | undefined,
  documentType: ApDocumentType,
) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { path, listKey } = AP_DOCUMENT_ENDPOINTS[documentType];
  return useMutation({
    mutationFn: (documentId: string) =>
      api
        .post<{
          agent_run_id: string;
        }>(`/businesses/${businessId}/ap/${path}/${documentId}/run_agent`)
        .then((response) => response.data),
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: agentRunsKey(businessId),
      });
      void queryClient.invalidateQueries({ queryKey: listKey(businessId) });
      void queryClient.invalidateQueries({
        queryKey: reviewQueueKey(businessId),
      });
    },
  });
};

/**
 * The approve refusal a reviewer can act on, or null for any other error. The
 * api client rejects with an `ApiError` carrying the response body as `data`.
 */
export const basisRefusal = (error: unknown): ApBasisRefusal | null => {
  if (!(error instanceof ApiError)) return null;
  const data = error.data as Partial<ApBasisRefusal> | undefined;
  return data?.code === "basis_changed" || data?.code === "basis_blocked"
    ? (data as ApBasisRefusal)
    : null;
};
